// =============================================================================
// Partner — Ready-to-Use accounts, credential checks and password resets
// =============================================================================
// The passcode rail stays in partnerClients.ts / partnerDrivers.ts untouched.
// Everything here is the second, additive account-creation path (migration 276)
// plus the credential-management actions that go with it.
//
// Passwords are returned by the server exactly once (creation / reset) and are
// never persisted in the app, in localStorage, or in logs.
// =============================================================================

import { getSupabaseClient } from "@veorest/api-client";
import { partnerApiPost } from "./partnerApi";
import type { TranslationKey } from "../i18n";

export interface AccountCredentials {
  username: string | null;
  password: string;
  shownOnce: true;
}

export interface HandleCheck {
  value: string | null;
  valid: boolean;
  available: boolean;
  error: string | null;
}

export interface CreateReadyClientInput {
  name: string;
  phone: string;
  address: string;
  slug: string;
  username: string;
  password: string;
  email?: string;
}

export interface CreateReadyDriverInput {
  name: string;
  phone: string;
  address?: string;
  vehicleType: "bicycle" | "motorcycle" | "car";
  vehicleModel?: string;
  vehicleNumber?: string;
  vehicleColor?: string;
  driverMode?: "own" | "paid";
  username: string;
  password: string;
}

/** Cryptographically strong, ambiguity-free password (12 chars). */
export function generatePassword(length = 12): string {
  const alphabet =
    "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%";
  const size = Math.min(24, Math.max(8, length));
  const bytes = new Uint8Array(size);
  crypto.getRandomValues(bytes);
  let out = "";
  for (let i = 0; i < size; i += 1) {
    out += alphabet[bytes[i]! % alphabet.length];
  }
  return out;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function mapHandle(raw: unknown): HandleCheck {
  const row = asRecord(raw) ?? {};
  return {
    value: row.value != null && String(row.value) !== "" ? String(row.value) : null,
    valid: row.valid === true,
    available: row.available === true,
    error: row.error != null ? String(row.error) : null,
  };
}

/** Live slug availability (partner-authenticated RPC — 276). */
export async function checkStoreSlug(
  partnerId: string,
  slug: string
): Promise<HandleCheck> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_check_store_slug", {
    p_partner_id: partnerId,
    p_slug: slug,
  });
  if (error) throw error;
  return mapHandle(data);
}

/** Live username availability for a store or driver (partner-authenticated RPC). */
export async function checkAccountUsername(
  partnerId: string,
  username: string,
  kind: "store" | "driver"
): Promise<HandleCheck> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_check_username", {
    p_partner_id: partnerId,
    p_username: username,
    p_kind: kind,
  });
  if (error) throw error;
  return mapHandle(data);
}

/** Ready-to-Use store: creates the Auth login + tenant in one server operation. */
export async function createReadyClient(
  partnerId: string,
  input: CreateReadyClientInput
): Promise<{ credentials: AccountCredentials; tenantId: string | null }> {
  const res = await partnerApiPost<{
    credentials?: Record<string, unknown>;
    client?: Record<string, unknown>;
  }>("/v1/partner/accounts/clients", {
    partnerId,
    name: input.name.trim(),
    phone: input.phone.trim(),
    address: input.address.trim(),
    email: input.email?.trim() || null,
    username: input.username.trim().toLowerCase(),
    slug: input.slug.trim().toLowerCase(),
    password: input.password,
  });

  const cred = asRecord(res.credentials) ?? {};
  const client = asRecord(res.client) ?? {};
  return {
    credentials: {
      username: cred.username != null ? String(cred.username) : null,
      password: String(cred.password ?? ""),
      shownOnce: true,
    },
    tenantId: client.tenant_id != null ? String(client.tenant_id) : null,
  };
}

/** Ready-to-Use driver: creates the Auth login + partner-fleet driver row. */
export async function createReadyDriver(
  partnerId: string,
  input: CreateReadyDriverInput
): Promise<{ credentials: AccountCredentials; driverId: string | null }> {
  const res = await partnerApiPost<{
    credentials?: Record<string, unknown>;
    driver?: Record<string, unknown>;
  }>("/v1/partner/accounts/drivers", {
    partnerId,
    name: input.name.trim(),
    phone: input.phone.trim(),
    address: input.address?.trim() || null,
    vehicleType: input.vehicleType,
    vehicleModel: input.vehicleModel?.trim() || null,
    vehicleNumber: input.vehicleNumber?.trim() || null,
    vehicleColor: input.vehicleColor?.trim() || null,
    driverMode: input.driverMode ?? null,
    username: input.username.trim().toLowerCase(),
    password: input.password,
  });

  const cred = asRecord(res.credentials) ?? {};
  const driver = asRecord(res.driver) ?? {};
  return {
    credentials: {
      username: cred.username != null ? String(cred.username) : null,
      password: String(cred.password ?? ""),
      shownOnce: true,
    },
    driverId: driver.driver_id != null ? String(driver.driver_id) : null,
  };
}

/** Partner-initiated reset for a linked store owner. */
export async function resetClientPassword(
  partnerId: string,
  tenantId: string,
  password?: string
): Promise<AccountCredentials> {
  const res = await partnerApiPost<{ credentials?: Record<string, unknown> }>(
    `/v1/partner/accounts/clients/${encodeURIComponent(tenantId)}/password`,
    { partnerId, password: password ?? null }
  );
  const cred = asRecord(res.credentials) ?? {};
  return {
    username: cred.username != null ? String(cred.username) : null,
    password: String(cred.password ?? ""),
    shownOnce: true,
  };
}

/** Partner-initiated reset for one of the partner's own fleet drivers. */
export async function resetDriverPassword(
  partnerId: string,
  driverId: string,
  password?: string
): Promise<AccountCredentials> {
  const res = await partnerApiPost<{ credentials?: Record<string, unknown> }>(
    `/v1/partner/accounts/drivers/${encodeURIComponent(driverId)}/password`,
    { partnerId, password: password ?? null }
  );
  const cred = asRecord(res.credentials) ?? {};
  return {
    username: cred.username != null ? String(cred.username) : null,
    password: String(cred.password ?? ""),
    shownOnce: true,
  };
}

/** Map a pre-flight handle error code to an i18n key (inline form feedback). */
export function handleErrorKey(error: string | null): TranslationKey {
  switch (error) {
    case "slug_taken":
      return "errors.slugTaken";
    case "slug_invalid":
    case "slug_reserved":
      return "errors.slugInvalid";
    case "username_taken":
      return "errors.usernameTaken";
    case "username_invalid":
    case "username_reserved":
      return "errors.usernameInvalid";
    default:
      return "errors.requestFailed";
  }
}

/** Map API/RPC failures to existing i18n keys. */
export function mapAccountError(err: unknown): TranslationKey {
  const msg =
    err instanceof Error && err.message
      ? err.message.toLowerCase()
      : String(err ?? "").toLowerCase();

  if (msg.includes("slug_taken")) return "errors.slugTaken";
  if (msg.includes("slug_reserved") || msg.includes("slug_invalid")) {
    return "errors.slugInvalid";
  }
  if (msg.includes("username_taken")) return "errors.usernameTaken";
  if (
    msg.includes("username_reserved") ||
    msg.includes("username_invalid") ||
    msg.includes("username_required")
  ) {
    return "errors.usernameInvalid";
  }
  if (msg.includes("password") || msg.includes("weak_password")) {
    return "errors.passwordTooWeak";
  }
  if (msg.includes("phone_taken")) return "errors.phoneTaken";
  if (msg.includes("phone")) return "errors.phoneInvalid";
  if (msg.includes("email")) return "errors.emailInvalid";
  if (msg.includes("login_missing")) return "errors.credentialsUnavailable";
  if (msg.includes("name_required")) return "errors.nameRequired";
  if (msg.includes("required")) return "errors.requiredFields";
  if (msg.includes("not_partner_user") || msg.includes("insufficient_partner_role")) {
    return "errors.forbidden";
  }
  if (msg.includes("client_not_found") || msg.includes("driver_not_found")) {
    return "errors.notFound";
  }
  if (msg.includes("rate_limited")) return "errors.rateLimited";
  if (msg.includes("auth_user_create_failed")) return "errors.requestFailed";
  if (msg.includes("not_authenticated") || msg.includes("unauthorized")) {
    return "errors.forbidden";
  }
  return "errors.requestFailed";
}
